import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Bot, Send, Loader2, Sparkles, User as UserIcon, MessageSquarePlus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED = [
  "Cosa dice la Legge 4/2013?",
  "Quali sono i requisiti per l'iscrizione al Registro?",
  "Fammi 5 domande di ripasso sul GDPR",
  "Cosa devo sapere sulla PEC?",
];

export default function AITutorSection() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/tutor/chat`,
        { message: msg, session_id: sessionId }, { withCredentials: true });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        text: "❌ " + (e.response?.data?.detail || "Errore. Riprova tra poco."),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const newChat = () => { setMessages([]); setSessionId(null); };

  return (
    <div data-testid="ai-tutor" className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col" style={{ height: "70vh", minHeight: 500 }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0a1f55] to-[#102a6b] text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#f59e0b] text-[#0a1020] flex items-center justify-center">
            <Bot className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-[#fbbf24]">Powered by Claude AI</div>
            <h3 className="font-extrabold">ACDT Tutor — Assistente Corso</h3>
          </div>
        </div>
        <button onClick={newChat} data-testid="new-chat" className="h-9 px-3 rounded-lg bg-white/15 hover:bg-white/25 text-white text-[12px] font-bold inline-flex items-center gap-1.5">
          <MessageSquarePlus className="h-3.5 w-3.5" /> Nuova chat
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Sparkles className="h-10 w-10 text-[#f59e0b] mx-auto mb-3" />
            <h4 className="font-extrabold text-[#0a1f55] text-lg">Ciao! Sono il tuo tutor del corso.</h4>
            <p className="text-[13px] text-slate-600 mt-1 max-w-md mx-auto">Posso rispondere a domande sui moduli, aiutarti nel ripasso e simulare quiz d&apos;esame.</p>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-xl mx-auto">
              {SUGGESTED.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid={`suggested-${s.slice(0,10)}`}
                  className="text-left rounded-xl border border-slate-200 bg-white p-3 text-[13px] hover:border-[#102a6b] hover:shadow transition">
                  <Sparkles className="h-3.5 w-3.5 text-[#f59e0b] inline mr-1.5" />{s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && (
              <div className="h-8 w-8 rounded-lg bg-[#0a1f55] text-white flex items-center justify-center flex-shrink-0"><Bot className="h-4 w-4" /></div>
            )}
            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-[13.5px] whitespace-pre-wrap ${
              m.role === "user" ? "bg-[#102a6b] text-white" : "bg-white border border-slate-200 text-slate-800"
            }`}>{m.text}</div>
            {m.role === "user" && (
              <div className="h-8 w-8 rounded-lg bg-[#f59e0b] text-white flex items-center justify-center flex-shrink-0"><UserIcon className="h-4 w-4" /></div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="h-8 w-8 rounded-lg bg-[#0a1f55] text-white flex items-center justify-center"><Bot className="h-4 w-4" /></div>
            <div className="bg-white border border-slate-200 rounded-2xl px-4 py-2.5 text-slate-500 text-[13px] inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sto pensando...
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 p-3 bg-white">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
          <input
            type="text" value={input} onChange={(e) => setInput(e.target.value)}
            data-testid="tutor-input"
            placeholder="Chiedi al tutor..."
            className="flex-1 h-11 px-4 rounded-xl border border-slate-300 focus:border-[#102a6b] focus:outline-none focus:ring-2 focus:ring-[#102a6b]/20 text-[14px]"
            disabled={loading}
          />
          <button type="submit" disabled={loading || !input.trim()} data-testid="tutor-send"
            className="h-11 px-4 rounded-xl bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold text-[13px] disabled:opacity-50 inline-flex items-center gap-1.5">
            <Send className="h-4 w-4" />
          </button>
        </form>
        <p className="text-[10.5px] text-slate-400 mt-2 text-center">Le risposte sono generate da AI e possono contenere imprecisioni. Verifica sempre le informazioni normative.</p>
      </div>
    </div>
  );
}
